import { Stage, StatePill } from "./Stage"
import { PrimaryActionRow, SubStageRow, BlockerAlert } from "./StagePrimitives"
import { FileText } from "lucide-react"
import { APCMState } from "../../types/apcm-ops.types"
import * as api from "../../api/apcm-ops.api"
import { useApcmOpsStore } from "../../stores/apcmOpsStore"

interface Props {
  orgId: string
  stateCounts: Record<string, number>
  pendingApprovalsCount: number
  missingChartData: number
  stuckGeneratingCount: number
  stuckUploadingCount: number
  onRefresh: () => void
  onViewApprovals: () => void
  onViewPatients: (states: string[]) => void
  onUploadCharts: () => void
}

export function CarePlanStage({
  orgId,
  stateCounts,
  pendingApprovalsCount,
  missingChartData,
  stuckGeneratingCount,
  stuckUploadingCount,
  onRefresh,
  onViewApprovals,
  onViewPatients,
  onUploadCharts,
}: Props) {
  const { executeAction, actionInProgress } = useApcmOpsStore()

  // States within this stage
  const pending = stateCounts[APCMState.CARE_PLAN_PENDING] || 0
  const generating = stateCounts[APCMState.CARE_PLAN_GENERATING] || 0
  const pendingApproval = stateCounts[APCMState.CARE_PLAN_PENDING_APPROVAL] || 0
  const approved = stateCounts[APCMState.CARE_PLAN_APPROVED] || 0
  const uploading = stateCounts[APCMState.CARE_PLAN_UPLOADING] || 0
  const uploaded = stateCounts[APCMState.CARE_PLAN_UPLOADED] || 0
  const failed = stateCounts[APCMState.CARE_PLAN_FAILED] || 0

  // Generate-eligible sources: CONSENT_OBTAINED + CARE_PLAN_PENDING + CARE_PLAN_FAILED
  const consented = stateCounts[APCMState.CONSENT_OBTAINED] || 0
  const generateEligible = consented + pending + failed

  // Total "in this stage" — everyone between consent and being ready for check-in
  const total =
    pending +
    generating +
    pendingApproval +
    approved +
    uploading +
    uploaded +
    failed

  return (
    <Stage
      number={3}
      accent="carePlan"
      icon={FileText}
      title="Care Plan"
      description="Generate care plans from charts, get clinical approval, upload to ECW."
      total={total}
    >
      {/* 3A — Generate */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
          Step A — Generate
        </div>
        <PrimaryActionRow
          label={generateEligible > 0 ? "ready to generate care plans" : "no patients ready to generate"}
          count={generateEligible}
          helpText={
            "Generates care plans for patients in CONSENT_OBTAINED, CARE_PLAN_PENDING, or CARE_PLAN_FAILED " +
            "who have chart data. Patients missing chart data are silently skipped — upload them first."
          }
          buttonLabel={
            generateEligible === 0
              ? "Nothing to do"
              : `Generate ${generateEligible} care plans`
          }
          onClick={async () => {
            await executeAction("Generate Care Plans", () => api.generateCarePlans(orgId))
            onRefresh()
          }}
          loading={actionInProgress === "Generate Care Plans"}
          disabled={generateEligible === 0}
        />
        {generating > 0 && (
          <div className="mt-1">
            <SubStageRow
              label="generating now — check back in a minute"
              count={generating}
              tone="running"
              helpText={
                stuckGeneratingCount > 0
                  ? `⚠ ${stuckGeneratingCount} stuck > 1h — check worker pods`
                  : undefined
              }
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CARE_PLAN_GENERATING])}
            />
          </div>
        )}
      </div>

      {/* 3B — Approve */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
          Step B — Approve
        </div>
        <PrimaryActionRow
          label={
            pendingApproval > 0
              ? "care plans awaiting clinical approval"
              : "nothing awaiting approval"
          }
          count={pendingApproval || pendingApprovalsCount}
          helpText="Review, download PDFs, and approve or reject before upload."
          buttonLabel={
            (pendingApproval || pendingApprovalsCount) === 0
              ? "No approvals needed"
              : `Review ${pendingApproval || pendingApprovalsCount} pending`
          }
          onClick={onViewApprovals}
          variant="ghost"
          disabled={(pendingApproval || pendingApprovalsCount) === 0}
        />
      </div>

      {/* 3C — Upload */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
          Step C — Upload to ECW
        </div>
        <PrimaryActionRow
          label={approved > 0 ? "approved plans ready to upload" : "nothing ready to upload"}
          count={approved}
          helpText="Uploads approved plans as DocumentReference via ECW FHIR API. Moves patients to CARE_PLAN_UPLOADED."
          buttonLabel={approved === 0 ? "Nothing to do" : `Upload ${approved} to ECW`}
          onClick={async () => {
            await executeAction("Upload to ECW", () => api.uploadCarePlans(orgId))
            onRefresh()
          }}
          loading={actionInProgress === "Upload to ECW"}
          disabled={approved === 0}
        />
        {uploading > 0 && (
          <div className="mt-1">
            <SubStageRow
              label="uploading now"
              count={uploading}
              tone="running"
              helpText={
                stuckUploadingCount > 0
                  ? `⚠ ${stuckUploadingCount} stuck > 1h — check ECW connectivity`
                  : undefined
              }
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CARE_PLAN_UPLOADING])}
            />
          </div>
        )}
        {uploaded > 0 && (
          <div className="mt-1">
            <SubStageRow
              label="uploaded — ready for check-in"
              count={uploaded}
              tone="success"
              helpText="Moves to next stage"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CARE_PLAN_UPLOADED])}
            />
          </div>
        )}
      </div>

      {/* Errors / blockers */}
      {failed > 0 && (
        <BlockerAlert
          severity="error"
          title={`${failed} care plan generation failed`}
          description="Usually timeout or missing chart data. Retrying re-queues generation via run-batch."
          actionLabel="Retry"
          onAction={async () => {
            await executeAction("Retry Failed", () => api.runBatch(orgId))
            onRefresh()
          }}
        />
      )}

      {missingChartData > 0 && (
        <BlockerAlert
          severity="warning"
          title={`${missingChartData} patient${missingChartData === 1 ? "" : "s"} missing chart data`}
          description={
            "Care plan generation will silently skip these patients. Upload their " +
            "FHIR charts (patient_health_summary) to unblock them — then re-run Generate."
          }
          actionLabel="Upload charts"
          onAction={onUploadCharts}
        />
      )}

      {/* Full state breakdown (progressive disclosure) */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {pending > 0 && <StatePill label="CARE_PLAN_PENDING" count={pending} tone="amber" />}
        {generating > 0 && (
          <StatePill label="CARE_PLAN_GENERATING" count={generating} tone="blue" />
        )}
        {pendingApproval > 0 && (
          <StatePill label="CARE_PLAN_PENDING_APPROVAL" count={pendingApproval} tone="amber" />
        )}
        {approved > 0 && <StatePill label="CARE_PLAN_APPROVED" count={approved} tone="emerald" />}
        {uploading > 0 && <StatePill label="CARE_PLAN_UPLOADING" count={uploading} tone="blue" />}
        {uploaded > 0 && <StatePill label="CARE_PLAN_UPLOADED" count={uploaded} tone="emerald" />}
        {failed > 0 && <StatePill label="CARE_PLAN_FAILED" count={failed} tone="red" />}
      </div>
    </Stage>
  )
}
